/**
 * Base Template Management State
 *
 * Defines the interface for all template management states (Platform, Club, Branch).
 * This implements the State design pattern to eliminate scattered if-else conditionals.
 */
export class TemplateManagementState {
  constructor(context) {
    this.context = context;
  }

  // ============================================================
  // API ENDPOINT METHODS
  // ============================================================

  /**
   * Get the base API endpoint for activity templates
   * @param {string} clubId - Club ID (required for club/branch contexts)
   * @returns {string} API endpoint
   */
  getActivityTemplateEndpoint(clubId = null) {
    throw new Error('Must implement getActivityTemplateEndpoint');
  }

  /**
   * Get the base API endpoint for session templates
   * @param {string} clubId - Club ID (required for club/branch contexts)
   * @returns {string} API endpoint
   */
  getSessionTemplateEndpoint(clubId = null) {
    throw new Error('Must implement getSessionTemplateEndpoint');
  }

  /**
   * Get the API endpoint for skills
   * @returns {string} API endpoint
   */
  getSkillsEndpoint() {
    throw new Error('Must implement getSkillsEndpoint');
  }

  // ============================================================
  // NAVIGATION PATH METHODS
  // ============================================================

  /**
   * Get navigation paths for activity templates
   * @returns {Object} Object with list, create, edit, view paths
   */
  getActivityNavigationPaths() {
    throw new Error('Must implement getActivityNavigationPaths');
  }

  /**
   * Get navigation paths for session templates
   * @returns {Object} Object with list, create, edit, view paths
   */
  getSessionNavigationPaths() {
    throw new Error('Must implement getSessionNavigationPaths');
  }

  /**
   * Get the back navigation path for the given template type
   * @param {string} templateType - 'activities' or 'sessions'
   * @returns {string} Back path
   */
  getBackPath(templateType) {
    throw new Error('Must implement getBackPath');
  }

  // ============================================================
  // UI CONFIGURATION METHODS
  // ============================================================

  /**
   * Get UI configuration for activity template management page
   * @returns {Object} UI config with labels, titles, etc.
   */
  getActivityManagementUIConfig() {
    throw new Error('Must implement getActivityManagementUIConfig');
  }

  /**
   * Get UI configuration for session template management page
   * @returns {Object} UI config with labels, titles, etc.
   */
  getSessionManagementUIConfig() {
    throw new Error('Must implement getSessionManagementUIConfig');
  }

  // ============================================================
  // DATA LOADING & FILTERING METHODS
  // ============================================================

  /**
   * Load available skills based on context
   * @param {Object} apiService - API service instance
   * @returns {Promise<Array>} Array of skill templates
   */
  async loadAvailableSkills(apiService) {
    throw new Error('Must implement loadAvailableSkills');
  }

  /**
   * Load available activity templates for session creation
   * @param {Object} apiService - API service instance
   * @param {string} clubId - Club ID
   * @returns {Promise<Array>} Array of activity templates
   */
  async loadAvailableActivityTemplates(apiService, clubId) {
    throw new Error('Must implement loadAvailableActivityTemplates');
  }

  /**
   * Separate templates into system and custom categories
   * @param {Array} allTemplates - All loaded templates
   * @returns {Object} Object with systemTemplates and customTemplates arrays
   */
  separateTemplates(allTemplates) {
    throw new Error('Must implement separateTemplates');
  }

  /**
   * Filter templates based on scope for this context
   * @param {Array} templates - Templates to filter
   * @returns {Array} Filtered templates
   */
  filterTemplatesByScope(templates) {
    throw new Error('Must implement filterTemplatesByScope');
  }

  // ============================================================
  // PERMISSION METHODS
  // ============================================================

  /**
   * Check if the user can edit a template
   * @param {Object} template - Template object with scope property
   * @returns {boolean} True if user can edit
   */
  canEdit(template) {
    throw new Error('Must implement canEdit');
  }

  /**
   * Check if the user can delete a template
   * @param {Object} template - Template object with scope property
   * @returns {boolean} True if user can delete
   */
  canDelete(template) {
    throw new Error('Must implement canDelete');
  }

  /**
   * Check if the user can copy a template
   * @param {Object} template - Template object with scope property
   * @returns {boolean} True if user can copy
   */
  canCopy(template) {
    throw new Error('Must implement canCopy');
  }

  /**
   * Get the copy action label
   * @returns {string} Label for copy button
   */
  getCopyLabel() {
    throw new Error('Must implement getCopyLabel');
  }

  // ============================================================
  // HELPER METHODS
  // ============================================================

  /**
   * Get the scope identifier for this context
   * @returns {string} 'platform', 'club', or 'branch'
   */
  getScope() {
    throw new Error('Must implement getScope');
  }
}

// ============================================================
// PLATFORM ADMIN STATE
// ============================================================

export class PlatformTemplateState extends TemplateManagementState {
  getActivityTemplateEndpoint() {
    return '/api/admin/activity-templates';
  }

  getSessionTemplateEndpoint() {
    return '/api/admin/session-templates';
  }

  getSkillsEndpoint() {
    return '/api/admin/skill-templates';
  }

  getActivityNavigationPaths() {
    return {
      list: '/admin/system-templates/activities',
      create: '/admin/system-templates/activities/create',
      edit: (id) => `/admin/system-templates/activities/${id}/edit`,
      view: (id) => `/admin/system-templates/activities/${id}`
    };
  }

  getSessionNavigationPaths() {
    return {
      list: '/admin/system-templates/sessions',
      create: '/admin/system-templates/sessions/create',
      edit: (id) => `/admin/system-templates/sessions/${id}/edit`,
      view: (id) => `/admin/system-templates/sessions/${id}`
    };
  }

  getBackPath(templateType) {
    return `/admin/system-templates/${templateType}`;
  }

  getActivityManagementUIConfig() {
    return {
      pageSubtitle: 'Manage platform-level activity templates available to all clubs',
      primaryActionLabel: 'CREATE TEMPLATE',
      customActivitiesTitle: null, // Platform doesn't show custom section
      systemTemplatesTitle: 'SYSTEM TEMPLATES',
      showSystemTemplatesSection: true,
      showCustomSection: false
    };
  }

  getSessionManagementUIConfig() {
    return {
      pageSubtitle: 'Manage platform-level session templates available to all clubs',
      primaryActionLabel: 'CREATE TEMPLATE',
      customSessionsTitle: null,
      systemTemplatesTitle: 'SYSTEM TEMPLATES',
      showSystemTemplatesSection: true,
      showCustomSection: false
    };
  }

  async loadAvailableSkills(apiService) {
    return await apiService.getSkillTemplates(true); // active_only=true
  }

  async loadAvailableActivityTemplates(apiService) {
    const response = await apiService.get('/api/admin/activity-templates?active_only=true');
    return response || [];
  }

  separateTemplates(allTemplates) {
    // Platform admin only sees platform templates
    const platformTemplates = allTemplates.filter(t => t.scope === 'platform');

    return {
      systemTemplates: platformTemplates,
      customTemplates: []
    };
  }

  filterTemplatesByScope(templates) {
    return templates.filter(t => t.scope === 'platform');
  }

  canEdit(template) {
    return template.scope === 'platform';
  }

  canDelete(template) {
    return template.scope === 'platform';
  }

  canCopy(template) {
    return false; // Platform admin doesn't copy
  }

  getCopyLabel() {
    return null;
  }

  getScope() {
    return 'platform';
  }
}

// ============================================================
// CLUB ADMIN STATE
// ============================================================

export class ClubTemplateState extends TemplateManagementState {
  getActivityTemplateEndpoint(clubId) {
    if (!clubId) throw new Error('Club ID required for club context');
    return `/api/clubs/${clubId}/activity-templates`;
  }

  getSessionTemplateEndpoint(clubId) {
    if (!clubId) throw new Error('Club ID required for club context');
    return `/api/clubs/${clubId}/session-templates`;
  }

  getSkillsEndpoint(clubId) {
    if (!clubId) throw new Error('Club ID required for club context');
    return `/api/clubs/${clubId}/skills`;
  }

  getActivityNavigationPaths() {
    return {
      list: '/admin/templates/activities',
      create: '/admin/templates/activities/create',
      edit: (id) => `/admin/templates/activities/${id}/edit`,
      view: (id) => `/admin/templates/activities/${id}`
    };
  }

  getSessionNavigationPaths() {
    return {
      list: '/admin/templates/sessions',
      create: '/admin/templates/sessions/create',
      edit: (id) => `/admin/templates/sessions/${id}/edit`,
      view: (id) => `/admin/templates/sessions/${id}`
    };
  }

  getBackPath(templateType) {
    return `/admin/templates/${templateType}`;
  }

  getActivityManagementUIConfig() {
    return {
      pageSubtitle: 'Manage system templates and custom club activities',
      primaryActionLabel: 'CREATE CUSTOM ACTIVITY',
      customActivitiesTitle: 'CLUB CUSTOM ACTIVITIES',
      systemTemplatesTitle: 'SYSTEM TEMPLATES',
      showSystemTemplatesSection: true,
      showCustomSection: true
    };
  }

  getSessionManagementUIConfig() {
    return {
      pageSubtitle: 'Manage system templates and custom club sessions',
      primaryActionLabel: 'CREATE CUSTOM SESSION',
      customSessionsTitle: 'CLUB CUSTOM SESSIONS',
      systemTemplatesTitle: 'SYSTEM TEMPLATES',
      showSystemTemplatesSection: true,
      showCustomSection: true
    };
  }

  async loadAvailableSkills(apiService) {
    return await apiService.listClubSkills(true); // active_only=true
  }

  async loadAvailableActivityTemplates(apiService, clubId) {
    if (!clubId) throw new Error('Club ID required');

    const response = await apiService.get(
      `/api/clubs/${clubId}/activity-templates?active_only=true`
    );
    let data = response || [];

    // Club admins see platform + club templates
    data = data.filter(template =>
      template.scope === 'platform' || template.scope === 'club'
    );

    return data;
  }

  separateTemplates(allTemplates) {
    // Club admin sees platform templates as system (read-only reference)
    // and club templates as editable custom activities
    const platformTemplates = allTemplates.filter(t => t.scope === 'platform');
    const clubTemplates = allTemplates.filter(t => t.scope === 'club');

    return {
      systemTemplates: platformTemplates,
      customTemplates: clubTemplates
    };
  }

  filterTemplatesByScope(templates) {
    return templates.filter(t =>
      t.scope === 'platform' || t.scope === 'club'
    );
  }

  canEdit(template) {
    return template.scope === 'club';
  }

  canDelete(template) {
    return template.scope === 'club';
  }

  canCopy(template) {
    return template.scope === 'platform';
  }

  getCopyLabel() {
    return 'Copy to Club';
  }

  getScope() {
    return 'club';
  }
}

// ============================================================
// BRANCH ADMIN STATE
// ============================================================

export class BranchTemplateState extends TemplateManagementState {
  getActivityTemplateEndpoint(clubId, branchId) {
    if (!branchId) throw new Error('Branch ID required for branch context');
    return `/api/branches/${branchId}/activity-templates`;
  }

  getSessionTemplateEndpoint(clubId, branchId) {
    if (!branchId) throw new Error('Branch ID required for branch context');
    return `/api/branches/${branchId}/session-templates`;
  }

  getSkillsEndpoint(clubId, branchId) {
    // Skills are club-level, not branch-level
    // Use /api/skills/club-skills endpoint instead
    return `/api/skills/club-skills`;
  }

  getActivityNavigationPaths(branchId) {
    if (!branchId) throw new Error('Branch ID required');
    return {
      list: `/branch/${branchId}/templates/activities`,
      create: `/branch/${branchId}/templates/activities/create`,
      edit: (id) => `/branch/${branchId}/templates/activities/${id}/edit`,
      view: (id) => `/branch/${branchId}/templates/activities/${id}`
    };
  }

  getSessionNavigationPaths(branchId) {
    if (!branchId) throw new Error('Branch ID required');
    return {
      list: `/branch/${branchId}/templates/sessions`,
      create: `/branch/${branchId}/templates/sessions/create`,
      edit: (id) => `/branch/${branchId}/templates/sessions/${id}/edit`,
      view: (id) => `/branch/${branchId}/templates/sessions/${id}`
    };
  }

  getBackPath(templateType, branchId) {
    if (!branchId) throw new Error('Branch ID required');
    return `/branch/${branchId}/templates/${templateType}`;
  }

  getActivityManagementUIConfig() {
    return {
      pageSubtitle: 'Manage club system templates and custom branch activities',
      primaryActionLabel: 'CREATE CUSTOM ACTIVITY',
      customActivitiesTitle: 'BRANCH ACTIVITIES',
      systemTemplatesTitle: 'CLUB TEMPLATES', // Branch sees club as system
      showSystemTemplatesSection: true,
      showCustomSection: true
    };
  }

  getSessionManagementUIConfig() {
    return {
      pageSubtitle: 'Manage club system templates and custom branch sessions',
      primaryActionLabel: 'CREATE CUSTOM SESSION',
      customSessionsTitle: 'BRANCH SESSIONS',
      systemTemplatesTitle: 'CLUB TEMPLATES', // Branch sees club as system
      showSystemTemplatesSection: true,
      showCustomSection: true
    };
  }

  async loadAvailableSkills(apiService, clubId, branchId) {
    // Skills are club-level, not branch-level
    // Branch admins and coaches access club skills
    return await apiService.listClubSkills(true); // active_only=true
  }

  async loadAvailableActivityTemplates(apiService, clubId, branchId) {
    if (!branchId) throw new Error('Branch ID required');

    const response = await apiService.get(
      `/api/branches/${branchId}/activity-templates?active_only=true`
    );
    let data = response || [];

    // Branch admins see club + branch templates (but NOT platform)
    data = data.filter(template =>
      template.scope === 'club' || template.scope === 'branch'
    );

    return data;
  }

  separateTemplates(allTemplates) {
    // Branch admin sees club templates as system (read-only reference)
    // and branch templates as editable custom activities
    // Platform templates are filtered out
    const clubTemplates = allTemplates.filter(t => t.scope === 'club');
    const branchTemplates = allTemplates.filter(t => t.scope === 'branch');

    return {
      systemTemplates: clubTemplates,
      customTemplates: branchTemplates
    };
  }

  filterTemplatesByScope(templates) {
    return templates.filter(t =>
      t.scope === 'club' || t.scope === 'branch'
    );
  }

  canEdit(template) {
    return template.scope === 'branch';
  }

  canDelete(template) {
    return template.scope === 'branch';
  }

  canCopy(template) {
    return template.scope === 'club';
  }

  getCopyLabel() {
    return 'Copy to Branch';
  }

  getScope() {
    return 'branch';
  }
}

// ============================================================
// COACH TEMPLATE STATE (Branch Level with Coach Navigation)
// ============================================================

/**
 * Coach Template State
 * 
 * Coaches access branch-level templates (same APIs as branch admins)
 * but with simplified navigation paths (/coach/templates instead of /branch/{id}/templates)
 * 
 * Permission rules for coaches:
 * - Can edit/delete only their own branch-level templates (created_by matches userId)
 * - Can view and copy all club and branch templates
 * - Cannot edit/delete templates created by branch admins or other coaches
 */
export class CoachTemplateState extends BranchTemplateState {
  getActivityNavigationPaths(branchId) {
    // Coaches use simplified /coach/ paths (no branchId in URL)
    return {
      list: '/coach/templates/activities',
      create: '/coach/templates/activities/create',
      edit: (id) => `/coach/templates/activities/${id}/edit`,
      view: (id) => `/coach/templates/activities/${id}`
    };
  }

  getSessionNavigationPaths(branchId) {
    // Coaches use simplified /coach/ paths (no branchId in URL)
    return {
      list: '/coach/templates/sessions',
      create: '/coach/templates/sessions/create',
      edit: (id) => `/coach/templates/sessions/${id}/edit`,
      view: (id) => `/coach/templates/sessions/${id}`
    };
  }

  canEdit(template) {
    // Coaches can only edit templates they created themselves
    return template.scope === 'branch' && template.created_by === this.context.userId;
  }

  canDelete(template) {
    // Coaches can only delete templates they created themselves
    return template.scope === 'branch' && template.created_by === this.context.userId;
  }

  getBackPath(templateType, branchId) {
    return `/coach/templates/${templateType}`;
  }

  getActivityManagementUIConfig() {
    return {
      pageSubtitle: 'Browse and use activity templates in your training sessions',
      primaryActionLabel: 'CREATE ACTIVITY',
      customActivitiesTitle: 'BRANCH ACTIVITIES',
      systemTemplatesTitle: 'CLUB TEMPLATES',
      showSystemTemplatesSection: true,
      showCustomSection: true,
      emptyStateMessage: 'No activity templates available',
      scopeLabels: {
        platform: 'System Template',
        club: 'Club Template',
        branch: 'Branch Template'
      }
    };
  }

  getSessionManagementUIConfig() {
    return {
      pageSubtitle: 'Browse and use session templates in your training plans',
      primaryActionLabel: 'CREATE SESSION',
      customSessionsTitle: 'BRANCH SESSIONS',
      systemTemplatesTitle: 'CLUB TEMPLATES',
      showSystemTemplatesSection: true,
      showCustomSection: true,
      emptyStateMessage: 'No session templates available',
      scopeLabels: {
        platform: 'System Template',
        club: 'Club Template',
        branch: 'Branch Template'
      }
    };
  }
}

// ============================================================
// STATE FACTORY
// ============================================================

/**
 * Factory function to create the appropriate state based on context
 * @param {Object} context - Context from useTemplateContext hook
 * @returns {TemplateManagementState} Appropriate state instance
 */
export function createTemplateState(context) {
  switch (context.contextType) {
    case 'platform':
      return new PlatformTemplateState(context);
    case 'club':
      return new ClubTemplateState(context);
    case 'branch':
      // Coaches use branch-level APIs but with coach-specific navigation
      return context.isCoach 
        ? new CoachTemplateState(context) 
        : new BranchTemplateState(context);
    default:
      throw new Error(`Unknown context type: ${context.contextType}`);
  }
}
